import React, { useState } from 'react';
import cn from 'classnames';
import { useLocation } from '@reach/router';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import Subtitle from 'components/UI/Subtitle';
import DesktopTabs from './DesktopTabs/DesktopTabs';
import MobileTabs from './MobileTabs/MobileTabs';
import TabPanel from './TabPanel';
import { replaceKeyword } from 'utils';
import { useLang } from 'utils/localisation';
import { usePageInterlinking } from 'staticQueries/page-interlinking-info';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './TabList.module.scss';

function TabList({ id, title, subtitle, items, page }) {
  const lang = useLang();
  const preparedLang = lang === 'uk' || lang === 'kz' ? 'en' : lang;
  const currentLink = useLocation().pathname;
  const [fadeInAnimation, setFadeInAnimation] = useState(false);

  const staticTabsData = removeSelfReferencingLinks(
    usePageInterlinking(page, preparedLang),
    currentLink,
  );
  const tabsData = items ?? staticTabsData;

  const [selectedTab, setSelectedTab] = useState(tabsData[0]);

  const content = { mainTitle: title, ...selectedTab };

  const setDesktopSelectedTab = payload => {
    setSelectedTab(payload);
    setFadeInAnimation(true);
  };

  const onAnimationEnd = () => {
    setFadeInAnimation(false);
  };

  return (
    <Container id={id ?? 'list-page-interlinking'} padding="Without" className={styles.wrapper}>
      <div className={styles.titleWrapper}>
        <Title tag="h2">{title}</Title>
        {subtitle && <Subtitle size="lg">{replaceKeyword(subtitle)}</Subtitle>}
      </div>
      <div className={styles.tabsSection}>
        <MobileTabs
          items={tabsData}
          selectedTab={selectedTab}
          setSelectedTabHandler={setSelectedTab}
          mainTitle={title}
          {...content}
        />
        <DesktopTabs
          tabs={tabsData}
          selectedTab={selectedTab}
          setSelectedTabHandler={setDesktopSelectedTab}
        />
      </div>
      <div
        className={cn(styles.content, fadeInAnimation && styles.fadeInAnimation)}
        onAnimationEnd={onAnimationEnd}
      >
        {tabsData.map((tabContent, index) => (
          <TabPanel key={index} index={index} tabContent={tabContent} selectedTab={selectedTab} />
        ))}
      </div>
    </Container>
  );
}

TabList.displayName = 'TabList';

export default withErrorBoundary(TabList);

function removeSelfReferencingLinks(arr, currentLink) {
  return arr?.filter(tabData => tabData.link !== currentLink);
}
